import { cn } from "@/lib/utils";
import { stageMeta } from "@/lib/domain-constants";
import { formatDate } from "@/lib/format";
import { registrationDeadline } from "@/lib/workadvisory-period";

export function translateSubject(subject?: string | null): string {
  switch ((subject ?? "").toLowerCase().trim()) {
    case "services":
      return "Servicio";
    case "works":
      return "Obra";
    case "goods":
      return "Bien";
    case "consulting":
      return "Consultoría de Obra";
    default:
      return subject ?? "";
  }
}

// ---- COLOR DE LAS PÍLDORAS ----
// El objeto contractual no significa nada bueno ni malo: va siempre en azul, y el
// texto de la píldora ya dice de qué se trata.
const BLUE_PILL =
  "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/20 dark:text-blue-300 dark:border-blue-900/50";

export function subjectClass(_subject?: string | null): string {
  return BLUE_PILL;
}

/**
 * La etapa sí significa algo, y su color lo dice: azul si la convocatoria sigue
 * abierta, verde si ya hay ganador y contrato, ámbar si el proceso está trabado o
 * se cayó. El mapa vive en `STAGE_MAP`.
 */
export function stageClass(stage?: string | null): string {
  return stageMeta(stage).badge;
}

/** Badge "Nueva": licitación añadida al sistema hace menos de 48h. */
export function NewBadge({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "shrink-0 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-700 dark:border-violet-900/50 dark:bg-violet-950/20 dark:text-violet-300",
        className,
      )}
    >
      Nueva
    </span>
  );
}

/**
 * Badge "Menor": contratación menor o igual a 8 UIT. Va en gris a propósito — es
 * un dato de contexto, no una señal de urgencia, y no debe competir con objeto
 * ni etapa.
 */
export function MinorBadge({ className }: { className?: string }) {
  return (
    <span
      title={
        "Contratación menor: compra del Estado por hasta 8 UIT. " +
        "Proceso más corto y con menos requisitos que una licitación, " +
        "y la ventana para cotizar suele ser de horas o pocos días."
      }
      className={cn(
        "shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-400",
        className,
      )}
    >
      Menor
    </span>
  );
}

export function SubjectPill({
  subject,
  className,
}: {
  subject?: string | null;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
        subjectClass(subject),
        className,
      )}
    >
      {translateSubject(subject)}
    </span>
  );
}

export function StagePill({
  stage,
  className,
}: {
  stage?: string | null;
  className?: string;
}) {
  return (
    <span
      title={stageMeta(stage).label}
      className={cn(
        "truncate rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
        stageClass(stage),
        className,
      )}
    >
      {stageMeta(stage).label}
    </span>
  );
}

/**
 * De dónde salió el monto cuando SEACE no publicó el suyo. Se dice en la
 * ETIQUETA del monto ("Valor referencial · bases"), no al lado de la cifra:
 * ahí competía con el número y el color lo hacía leer como una alerta.
 */
/**
 * "Registro hasta 23 sep 2026", o "Registro cerrado · 23 sep 2026". Va bajo la etapa
 * de las convocadas: explica por qué una obra no se cruzó.
 */
export function RegistrationEnd({ endsAt }: { endsAt: string }) {
  const closed = new Date(endsAt) < new Date();
  const urgent = !closed && registrationDeadline(endsAt)?.urgent;
  return (
    <span
      className={cn(
        "text-[10px] leading-tight",
        closed
          ? "text-slate-400"
          : urgent
            ? "font-semibold text-rose-600 dark:text-rose-400"
            : "text-slate-500 dark:text-slate-400",
      )}
    >
      {closed ? "Registro cerrado · " : "Registro hasta "}
      {formatDate(endsAt)}
    </span>
  );
}

export const FROM_BASES_HINT =
  "Monto leído de las bases del proceso; SEACE no publicó el suyo.";

/** Lo mismo para la tarjeta del tablero, que pinta el monto sin etiqueta. */
export function FromBasesNote({ className }: { className?: string }) {
  return (
    <span
      title={FROM_BASES_HINT}
      className={cn(
        "cursor-help text-[11px] leading-tight text-slate-400 dark:text-slate-500",
        className,
      )}
    >
      según las bases
    </span>
  );
}
